import { useRef, useEffect } from "react";
import useSpeech from "./useSpeech";
import useTapOrHold from "./useTapOrHold";
import { useGetState } from "components/ReduxProvider";
import { usePrevious } from "react-use";
import { useViewerDecks } from "queries/viewerQuery";
import { useDeviceLang } from "./deviceHooks";
import { compact, uniq } from "lodash";

type SpeechTapHoldOptions = {
  languages?: string[];
  stopAfterMs?: number;
  transcription?: boolean;
  onSpeechData?: (audioBase64: string) => void;
  onTranscription?: (text: string) => void;
  onStart?: () => void;
  onStop?: () => void;
  forceMp3?: boolean;
};

export default function useSpeechTapHold({
  languages,
  stopAfterMs,
  transcription,
  onSpeechData,
  onTranscription,
  onStart,
  onStop,
  forceMp3
}: SpeechTapHoldOptions) {
  const globalRecording = useGetState("isAudioRecording");
  const ref = useRef<HTMLButtonElement>(null);
  const { deck } = useViewerDecks();
  const { appLang } = useDeviceLang();
  const defaultLanguages = uniq(compact([deck?.front, deck?.back, appLang]));

  const { transcribing, listening, supported, startListening, stopListening, cancelListening } = useSpeech({
    onSpeechData: onSpeechData,
    onTranscribed: onTranscription,
    transcription,
    languages: languages || defaultLanguages,
    listeningOptions: {
      stopAfterMs,
      sourceElementRef: ref
    },
    forceMp3
  });
  const prevListening = usePrevious(listening);

  const otherRecording = globalRecording ? !listening : false;

  const { handlers, resetPressState } = useTapOrHold({
    startAction: () => {
      onStart?.();
      startListening();
    },
    endAction: () => {
      stopListening();
    },
    disabled: otherRecording,
    holdThresholdMs: 1000 // 1 second, for some users 600ms is too short, and in this case is OK to have this higher threshold
  });

  // listening can be closed externally, so we need to reset the press state
  useEffect(() => {
    if (!listening && prevListening) {
      resetPressState();
      onStop?.();
    }
  }, [listening, prevListening, resetPressState, onStop]);

  return {
    // states
    listening,
    transcribing,
    supported,
    globalRecording,
    otherRecording,
    // methods
    startListening,
    stopListening,
    cancelListening,
    // pass these handlers down to your UI
    handlers: {
      ref,
      ...handlers
    }
  };
}
